import catchify from 'catchify';
import get from 'lodash/get';

import { extractBrowse } from '@/util/global';
import favicon from '~/static/favicon.svg';

import NavBarHeader from '../components/common/NavBarHeader';
import Footer from '../components/common/Footer';

export default {
  components: {
    Footer,
    NavBarHeader,
  },
  async fetch() {
    const [error, response] = await catchify(
      this.$cachios.get('/api/banners/?format=json'),
    );

    if (error || !(response && response.data)) return;

    this.banners = response.data?.results || [];

    if (this.getCurrentUser?.is_staff) {
      this.$store.commit('wishlists/setWishlistExperiment', this.$config.showWishilist);
    }
  },
  data() {
    return {
      banners: [],
      csrf: null,
    };
  },
  watch: {
    allowStickyHeader(allowSticky) {
      if (allowSticky) {
        setTimeout(() => {
          const navbar = document.querySelector('.navbar.fixed-top') || document.querySelector('body.with_fixed_header .new-navbar');

          if (navbar) {
            navbar.classList.remove('scroll-down');
            navbar.classList.remove('scrolling');
            document.body.classList.remove('navbar-visible');
          }
        }, 30); // wait until after scroller has potentially fired to avoid race
      }
    },
  },
  computed: {
    displayOneTap() {
      return !this.getCurrentUser
        && !!this.$env?.GOOGLE_DATA_CLIENT_ID
        && !this.$route?.path.includes('/products');
    },
    previewCampaign() {
      return this.$store.state.session?.session?.preview_campaign || false;
    },
    previewCampaignName() {
      return this.$store.state.session?.session?.preview_campaign?.description || '';
    },
    campaignStyle() {
      return {
        position: 'fixed',
        zIndex: 9999999,
        top: 0,
        left: 0,
        background: '#3498db',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '12px',
        padding: '2px 10px 2px 10px',
        borderBottomRightRadius: '3px',
      };
    },
    isHijackedUser() {
      return get(this, ['$store', 'state', 'session', 'session', 'is_hijacked_user']);
    },
    getHijackedUserRedirectPath() {
      return get(this, ['$store', 'state', 'session', 'session', 'hijacked_user_redirect_path']);
    },
    getCurrentUser() {
      return get(this, ['$store', 'state', 'session', 'user']);
    },
    wishlistExperiment() {
      return this.$store.getters['wishlists/wishlistExperiment'];
    },
    allowStickyHeader() {
      // disable sticky header on PDP
      return this.$route.name !== 'products-dealSlug';
    },
    browse() {
      return extractBrowse(this.$route?.params.pathMatch);
    },
    showOverlay() {
      return this.$store.getters.showBodyOverlay || false;
    },
  },
  async created() {
    if (process.browser) {
      /* eslint-disable global-require */
      require('~/util/navbar-scroll.js');
      require('~/util/banner.js');
      /* eslint-enable global-require */
    }
  },
  mounted() {
    // viewport dimensions can be incorrectly calculated on mobile browsers (e.g. mobile chrome),
    // so we are including a listener to responsively calculate viewport height
    // read more: https://dev.to/admitkard/mobile-issue-with-100vh-height-100-100vh-3-solutions-3nae
    window.addEventListener('resize', this.setVhVariable);
    this.setVhVariable();

    const { session } = this.$store.state;

    this.csrf = this.$cookies.get('csrftoken');

    if (window && this.banners.length > 0) {
      window.bannersJs = [...this.banners];
    }

    this.$gtm.init();

    if (session.session && session.session.heap_enabled && this.$env.HEAP_APP_ID) {
      this.initHeapAnalytics(this.$env.HEAP_APP_ID);
    }

    if (session.messages) {
      session.messages.forEach(({ tags, message }) => {
        // Update for new toastr tags
        let tag = 'i';

        switch (tags) {
          case 'success': tag = 's'; break;
          case 'error': tag = 'e'; break;
          case 'warning': tag = 'w'; break;

          default: break;
        }

        const fn = this.$toastr[tag];

        fn(message);
      });

      // let's clear these out after displaying so if navigating home <--> browse for ex,
      // we don't show duplicate toasts to our users
      this.$store.dispatch('clearSessionMessages');
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setVhVariable);
  },
  methods: {
    setVhVariable() {
      document.querySelector(':root').style.setProperty('--vh', `${window.innerHeight}px`);
    },
    initHeapAnalytics(appId, config = {}) {
      window.heap = window.heap || [];
      window.heap.appid = appId;
      window.heap.config = config;

      // NOTE: disabling eslint for this block since it's provided by heap
      /* eslint-disable */
      const r = document.createElement('script');
      r.type = 'text/javascript';
      r.async = !0;
      r.src = `https://cdn.heapanalytics.com/js/heap-${appId}.js`;

      const a = document.getElementsByTagName('script')[0];
      a.parentNode.insertBefore(r, a);
      for (let n = function (e) {
        // eslint-ignore-next-line func-names
        return function () { heap.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
      },
        p = [
          'addEventProperties',
          'addUserProperties',
          'clearEventProperties',
          'identify',
          'resetIdentity',
          'removeEventProperty',
          'setEventProperties',
          'track',
          'unsetEventProperty',
        ], o = 0; o < p.length; o++) heap[p[o]] = n(p[o]);
      /* eslint-enable */
    },
    checkForRepeatUserData(dataArray) {
      return !!dataArray.find((dataObj) => dataObj.userId);
    },
    getReleaseUserUrl() {
      const baseRedirect = '/hijack/release-hijack/?next=';
      return `${baseRedirect}${this.getHijackedUserRedirectPath}`;
    },
  },
  head() {
    const path = this.$route.path.replace(/\/?$/, '/');

    return {
      bodyAttrs: {
        class: ['with_fixed_header', 'with-fixed-header'],
      },
      link: [
        {
          hid: 'icon',
          rel: 'icon',
          type: 'image/svg+xml',
          href: favicon,
        },
        {
          hid: 'search',
          rel: 'search',
          type: 'application/opensearchdescription+xml',
          href: '/opensearch.xml',
        },
        {
          hid: 'canonical',
          rel: 'canonical',
          href: `https://appsumo.com${path}`,
        },
      ],
      script: [
        {
          async: true,
          defer: true,
          src: 'https://accounts.google.com/gsi/client',
        },
      ],
      title: 'Stop overpaying for software. | AppSumo',
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: 'Top software deals for entrepreneurs at incredible prices. Discover the latest productivity, marketing, and content tools for your business with no monthly fees.',
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: 'Stop overpaying for software. | AppSumo',
        },
        {
          property: 'og:site_name',
          content: 'AppSumo',
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: 'Top software deals for entrepreneurs at incredible prices. Discover the latest productivity, marketing, and content tools for your business with no monthly fees.',
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: 'https://appsumo2-cdn.appsumo.com/static/images/meta/appsumo-logo-meta.png',
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `https://appsumo.com${path}`,
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:email',
          content: 'support@appsumo.com',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:site',
          content: '@appsumo',
        },
        {
          hid: 'twitter:url',
          name: 'twitter:url',
          content: `https://appsumo.com${path}`,
        },
        {
          name: 'twitter:creator',
          content: 'appsumo',
        },
      ],
    };
  },
};
